import axios from 'axios';
import type { MemberUser, User } from './types';

/**
 * Whether a validateToken rejection means the session itself is dead.
 *
 * The api answers 401 for a token-level rejection; 403 is still accepted so a client deployed
 * ahead of the api keeps logging the user out. The interceptor discriminates a per-resource 403
 * by URL before calling this; `makeShouldRetryQuery` does not, so such a 403 is also not retried.
 */
export function isDeadSessionRejection(e: unknown): boolean {
  if (!axios.isAxiosError(e)) {
    return false;
  }
  const status = e.response?.status;
  return status === 401 || status === 403;
}

/** Builds react-query's `retry` predicate, taking the limit so tests need not match the DEV value. */
export function makeShouldRetryQuery(maxRetries: number) {
  return (failureCount: number, error: unknown): boolean => {
    if (isDeadSessionRejection(error)) {
      return false;
    }
    return failureCount < maxRetries;
  };
}

/** Retries a failed query three times outside dev, and never once the session is dead. */
export const shouldRetryQuery = makeShouldRetryQuery(import.meta.env.DEV ? 0 : 3);

/** 403 on a normal API call: keep session, warn, and send user back to the app home. */
export function handleForbiddenResource() {
  window.alert(
    "You don't have access to this resource. If you believe this is an error, please contact your administrator.",
  );
  window.location.pathname = '/home';
}

export function getAvatarLetters(user: User | MemberUser): string {
  if (user.name === '') {
    return user.email.charAt(0);
  }

  const splits = user.name.split(' ');
  if (splits.length === 1) {
    return splits[0].charAt(0);
  } else {
    return `${splits[0].charAt(0)}${splits[splits.length - 1].charAt(0)}`;
  }
}
